import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api, { authApi } from '@/lib/api';

// Access-token JWT lifetime (backend: 180s). We do a silent refresh before it expires.
const ACCESS_TOKEN_SECONDS = 180;
// Show warning WARN_BEFORE_EXPIRY_SECONDS before the *refresh cookie* would expire
// (backend: 30 min sliding). We don't know the exact expiry from JS (httpOnly cookie),
// so we mirror it locally via lastActivityAt and start warning close to that boundary.
const REFRESH_MAX_MINUTES = 30;
const WARN_BEFORE_EXPIRY_SECONDS = 120; // 2 min heads-up before hard cap

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);

  const refreshTimerRef = useRef(null);
  const sessionExpiryRef = useRef(null); // absolute ms timestamp for hard expiry
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const handleSessionExpired = useCallback(() => {
    clearTimers();
    setShowSessionWarning(false);
    setUser(null);
    setTenant(null);
    window.location.href = '/login?expired=true';
  }, [clearTimers]);

  // Absolute 30-min session cap warning
  const armExpiryWarning = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    const expiresAt = sessionExpiryRef.current;
    if (!expiresAt) return;
    const msUntilWarn = expiresAt - Date.now() - WARN_BEFORE_EXPIRY_SECONDS * 1000;
    warningTimerRef.current = setTimeout(() => {
      setShowSessionWarning(true);
      setSessionTimeRemaining(WARN_BEFORE_EXPIRY_SECONDS);
      countdownRef.current = setInterval(() => {
        setSessionTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            handleSessionExpired();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, Math.max(0, msUntilWarn));
  }, [handleSessionExpired]);

  // Called every time the refresh cookie is renewed (backend slides it +30min).
  // Must run on EVERY successful refresh (automatic timer AND 401-triggered),
  // otherwise the local 30-min warning fires even while the user is active.
  const bumpSessionExpiry = useCallback(() => {
    sessionExpiryRef.current = Date.now() + REFRESH_MAX_MINUTES * 60 * 1000;
    armExpiryWarning();
  }, [armExpiryWarning]);

  // Silent refresh: renew the access cookie ~20s before it expires. On a
  // transient network failure, we retry once after a short delay before
  // giving up — a single hiccup should not end the user's session, which was
  // causing the "forced logout" pattern reported by super admins.
  const scheduleSilentRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(async () => {
      const doRefresh = async () => api.post('/auth/refresh');
      try {
        await doRefresh();
      } catch (err) {
        // Only retry on transient (no HTTP response) failures. A 401/403
        // means the session is truly gone and there is no point retrying.
        const status = err?.response?.status;
        if (!status) {
          try {
            await new Promise((r) => setTimeout(r, 3000));
            await doRefresh();
            // Successful recovery after a hiccup — reassure super admins who
            // otherwise saw the session drop silently.
            toast.success('Sessão restaurada', {
              description: 'A ligação foi recuperada. Pode continuar a trabalhar.',
              duration: 3500,
            });
          } catch (_) {
            clearTimers();
            setUser(null);
            setTenant(null);
            return;
          }
        } else {
          clearTimers();
          setUser(null);
          setTenant(null);
          return;
        }
      }
      bumpSessionExpiry(); // keep the 30-min window sliding while active
      scheduleSilentRefresh(); // chain
    }, Math.max(5, ACCESS_TOKEN_SECONDS - 20) * 1000);
  }, [clearTimers, bumpSessionExpiry]);

  // When the browser tab becomes visible again, background setTimeout may
  // have been throttled and the access cookie may already be expired. Fire
  // an immediate refresh (best effort) so the next user action doesn't fail
  // with a spurious 401 → redirect to /login.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!user) return;
      try {
        await api.post('/auth/refresh');
        bumpSessionExpiry();
        scheduleSilentRefresh();
      } catch (_) {
        // Let the axios interceptor handle any subsequent 401.
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [user, bumpSessionExpiry, scheduleSilentRefresh]);

  // Initial hydration: if the cookie is present, /auth/me succeeds and we get user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.me();
        if (cancelled) return;
        setUser(me.data);
        if (me.data.tenant_slug) {
          setTenant({
            slug: me.data.tenant_slug,
            name: me.data.tenant_name,
            is_demo: !!me.data.tenant_is_demo,
          });
        }
        bumpSessionExpiry();
        scheduleSilentRefresh();
      } catch (_) {
        // no session
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; clearTimers(); };
  }, [bumpSessionExpiry, scheduleSilentRefresh, clearTimers]);

  // The axios interceptor in lib/api.js also refreshes the access cookie
  // (on a 401 retry). Mirror that into the local 30-min session window too.
  useEffect(() => {
    window.addEventListener('auth:session-refreshed', bumpSessionExpiry);
    return () => window.removeEventListener('auth:session-refreshed', bumpSessionExpiry);
  }, [bumpSessionExpiry]);

  const login = useCallback(async (email, password, tenantSlug = null, mfaCode = null) => {
    try {
      const payload = { email, password, mfa_code: mfaCode };
      if (tenantSlug) payload.tenant_slug = tenantSlug;
      const { data } = await api.post('/auth/login', payload);
      setUser(data.user);
      setTenant(data.tenant || null);
      bumpSessionExpiry();
      scheduleSilentRefresh();
      return { success: true, role: data.user?.role, mustChangePassword: !!data.user?.must_change_password, isCognisegGlobal: !!data.user?.is_cogniseg_global };
    } catch (error) {
      if (error.response?.status === 428 && error.response?.data?.detail === 'MFA_REQUIRED') {
        return { success: false, mfaRequired: true, error: 'Código MFA necessário' };
      }
      // Rate-limit response — surface Retry-After (in seconds) so the UI can
      // show a countdown and disable the form.
      if (error.response?.status === 429) {
        const header = error.response?.headers?.['retry-after'];
        const retryAfter = Number(header) || 0;
        return {
          success: false,
          mfaRequired: false,
          rateLimited: true,
          retryAfter,
          error: error.response?.data?.detail || 'Demasiadas tentativas. Tente novamente daqui a instantes.',
        };
      }
      return {
        success: false,
        mfaRequired: false,
        error: error.response?.data?.detail || 'Erro ao fazer login'
      };
    }
  }, [bumpSessionExpiry, scheduleSilentRefresh]);

  const logout = useCallback(async () => {
    clearTimers();
    setShowSessionWarning(false);
    setUser(null);
    setTenant(null);
    try { await authApi.logout(); } catch (_) { /* ignore */ }
  }, [clearTimers]);

  const confirmSessionRefresh = useCallback(async () => {
    setShowSessionWarning(false);
    try {
      await api.post('/auth/refresh');
      bumpSessionExpiry();
      scheduleSilentRefresh();
      return true;
    } catch (_) {
      handleSessionExpired();
      return false;
    }
  }, [bumpSessionExpiry, scheduleSilentRefresh, handleSessionExpired]);

  const declineSessionRefresh = useCallback(() => {
    setShowSessionWarning(false);
    handleSessionExpired();
  }, [handleSessionExpired]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authApi.me();
      setUser(data);
      return data;
    } catch (_) { return null; }
  }, []);

  // Used by the CogniSeg tenant picker to hydrate the tenant slot after
  // /api/cogniseg/select-tenant returns a tenant-scoped JWT.
  const setTenantContext = useCallback((tenantObj) => {
    setTenant(tenantObj || null);
  }, []);

  const hasRole = useCallback((...roles) => user && roles.includes(user.role), [user]);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        // Legacy field kept so existing components reading `token` don't crash;
        // cookies are the source of truth, this flag is truthy while user is set.
        token: isAuthenticated ? 'cookie' : null,
        loading,
        isAuthenticated,
        login,
        logout,
        refreshUser,
        setTenantContext,
        hasRole,
        showSessionWarning,
        sessionTimeRemaining,
        confirmSessionRefresh,
        declineSessionRefresh
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
